import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { useWorkoutSession } from '../hooks/useWorkoutSession';
import { formatSecs, useRestTimer } from '../hooks/useRestTimer';
import RestFill from '../components/RestFill';
import Stepper from '../components/Stepper';
import { formatPct, warmupPyramid } from '../warmup';
import { startingReps, startingWarmupReps } from '../reps';
import { applyWorkingWeight, clampSets, nextSetLabel, resizeSection, stepValue } from '../sets';
import { autofillFromPrevious, compareToPrevious, previousSetFor } from '../previous';
import { usePreviousPerformance } from '../hooks/usePreviousPerformance';
import { clearJson, readJson, restTimerKey, restWindowKey, writeJson } from '../persistentTime';
import InfoButton from '../components/InfoButton';
import VideoButton from '../components/VideoButton';
import { explainTechnique } from '../techniques';
import type { SessionExercise, SetLog } from '../../../shared/types';

/** Weight always moves in 5s; hold the +/- buttons to travel quickly. */
const WEIGHT_STEP = 5;

/**
 * The in-gym screen for a single exercise.
 *
 * Two phases, both full-page:
 *   pick    - choose one of the three options, then start
 *   running - log each set, with a rest countdown between them
 *
 * Numbers are entered with +/- steppers, so no keyboard ever opens.
 */
export default function ExercisePage() {
  const { sessionId, exerciseIndex } = useParams<{ sessionId: string; exerciseIndex: string }>();
  const navigate = useNavigate();
  const { session, loading, error, saveError, mutate, flush } = useWorkoutSession(sessionId);

  const index = Number(exerciseIndex);
  const exercise = session?.exercises.find(e => e.index === index);

  const [picking, setPicking] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  // Which exercise the picking/selected state was set up for. React Router
  // reuses this component across a route-param change (exercise → exercise), so
  // without tracking this the previous exercise's view and chosen option leak
  // into the next one — the reported "skips the variation picker" bug.
  const setupForIndex = useRef<number | null>(null);
  if (exercise && setupForIndex.current !== index) {
    // Adjusting state during render is React's documented pattern for resetting
    // on a prop change, and avoids a flash of the previous exercise's view.
    setupForIndex.current = index;
    setPicking(!exercise.sets.some(s => s.completed));
    setSelected(exercise.exerciseName);
  }

  // Sessions created before the set counts were bounded can hold more sets
  // than the program allows, which used to make every later save fail. Heal
  // them once on load; clampSets only drops empty, incomplete sets.
  useEffect(() => {
    if (!exercise) return;
    const healed = clampSets(exercise.sets, exercise);
    if (healed !== exercise.sets) {
      mutate(list => list.map(e => (e.index === index ? { ...e, sets: healed } : e)));
    }
  }, [exercise, index, mutate]);

  const { previousSets, previousPerformedAt, previousLoaded } =
    usePreviousPerformance(exercise?.exerciseName);

  // Pre-fill from the last time this exercise was done. autofillFromPrevious
  // only writes into untouched sets and returns the same array when there is
  // nothing to do, so this cannot clobber work or loop.
  useEffect(() => {
    if (!exercise || !previousLoaded || previousSets.length === 0) return;
    const filled = autofillFromPrevious(exercise.sets, previousSets, exercise);
    if (filled !== exercise.sets) {
      mutate(list => list.map(e => (e.index === index ? { ...e, sets: filled } : e)));
    }
  }, [exercise, previousSets, previousLoaded, index, mutate]);

  // A rest carried in from the previous exercise is still counting down that
  // exercise's prescription, so its window travels with it rather than being
  // read from whatever is now on screen.
  const [restWindow, setRestWindow] = useState<{ low: number | null; high: number | null } | null>(
    () => (sessionId ? readJson(restWindowKey(sessionId)) : null),
  );

  const rest = useRestTimer(
    restWindow ? restWindow.low : exercise?.restLowSecs,
    restWindow ? restWindow.high : exercise?.restHighSecs,
    sessionId ? restTimerKey(sessionId) : undefined,
  );

  const nextSet = useMemo(
    () => exercise?.sets.find(s => !s.completed) ?? null,
    [exercise],
  );

  if (loading) return <><SiteHeader /><main className="page page--narrow"><p className="muted">Loading…</p></main></>;
  if (error) return <><SiteHeader /><main className="page page--narrow"><p className="error">{error}</p></main></>;
  if (!session || !exercise) {
    return <><SiteHeader /><main className="page page--narrow"><p className="muted">Exercise not found.</p></main></>;
  }

  const updateExercise = (fn: (e: SessionExercise) => SessionExercise, save = false) =>
    mutate(list => list.map(e => (e.index === index ? fn(e) : e)), save);

  /** `save` is for the few moments worth a round trip, not every tap. */
  const updateSets = (fn: (sets: SetLog[]) => SetLog[], save = false) =>
    updateExercise(e => ({ ...e, sets: fn(e.sets) }), save);

  const warmupSets = exercise.sets.filter(s => s.warmup);
  const workingSets = exercise.sets.filter(s => !s.warmup);
  const pyramid = warmupPyramid(warmupSets.length);

  // Bounds come from the program (server-enforced); working sets are a fixed
  // count, warm-ups are usually a range.
  // Only the warm-up bounds drive UI now: the working set count is fixed by
  // the program, so nothing on this screen can change it.
  const warmupMin = exercise.warmupMin ?? warmupSets.length;
  const warmupMax = exercise.warmupMax ?? warmupSets.length;

  /** The reps a set starts on when nothing has been entered yet. */
  function defaultReps(set: SetLog): number | null {
    if (set.warmup) {
      const pos = warmupSets.findIndex(s => s.setNumber === set.setNumber);
      return startingWarmupReps(pyramid[pos]?.reps);
    }
    const pos = workingSets.findIndex(s => s.setNumber === set.setNumber);
    return startingReps(exercise!.targetReps, Math.max(0, pos));
  }

  const shownReps = (set: SetLog) => set.reps ?? defaultReps(set);

  const lastWorking = workingSets[workingSets.length - 1];
  const isLastWorking = (set: SetLog) =>
    !set.warmup && lastWorking != null && set.setNumber === lastWorking.setNumber;

  function stepWeight(set: SetLog, delta: number) {
    updateSets(sets => {
      // Deliberately read from `sets`, not the captured `set`: while a button
      // is held, this closure is the one from the render where the press
      // began, so `set.weight` never advances and every repeat would write the
      // same value.
      const current = sets.find(s => s.setNumber === set.setNumber)?.weight;
      return applyWorkingWeight(sets, set.setNumber, stepValue(current, delta));
    });
  }

  function stepReps(set: SetLog, delta: number) {
    updateSets(sets => sets.map(s => {
      if (s.setNumber !== set.setNumber) return s;
      const current = s.reps ?? defaultReps(s) ?? 0;
      return { ...s, reps: Math.max(1, current + delta) };
    }));
  }

  function chooseAndStart() {
    if (selected) updateExercise(e => ({ ...e, exerciseName: selected }));
    setPicking(false);
  }

  function completeSet(set: SetLog) {
    updateSets(sets => sets.map(s => s.setNumber === set.setNumber
      // Persist the shown reps so an untouched default is still recorded.
      ? { ...s, completed: true, reps: s.reps ?? defaultReps(s) ?? undefined, completedAt: Math.floor(Date.now() / 1000) }
      : s), true);
    const window = {
      low: exercise!.restLowSecs ?? null,
      high: exercise!.restHighSecs ?? null,
    };
    if (sessionId) writeJson(restWindowKey(sessionId), window);
    setRestWindow(window);
    // Always rest, including after the final set — that is the rest that
    // carries over into the next exercise.
    rest.start();
  }

  const resize = (warmup: boolean, count: number) =>
    updateSets(sets => resizeSection(sets, warmup, count));

  function finishExercise(skipped = false) {
    updateExercise(e => ({ ...e, completed: !skipped, skipped: skipped || undefined }));
    // Fire the save but do NOT wait for it before navigating — blocking the
    // button on a network round trip was the reported "takes a long time to
    // load the next page" latency. The component stays mounted across the
    // route change, so the in-flight save still completes.
    void flush();
    const next = session!.exercises.find(e => e.index !== index && !e.completed && !e.skipped);
    navigate(next ? `/workout/${session!.sessionId}/${next.index}` : `/workout/${session!.sessionId}`);
  }

  // The prescribed technique, matched to its explanation (null when there is
  // nothing to explain, e.g. "N/A").
  const explained = explainTechnique(exercise.technique);

  const allSetsDone = exercise.sets.every(s => s.completed);
  // What the lifter is about to do — shown once a rest is over, including a
  // rest carried in from the previous exercise.
  const upNext = nextSetLabel(exercise.sets);

  // Step back through the day in order. Before the first exercise sits the
  // warm-up, so that is where "back" goes from there. Uses list position
  // rather than index arithmetic, which would assume contiguous numbering.
  const position = session.exercises.findIndex(e => e.index === index);
  const previousExercise = position > 0 ? session.exercises[position - 1] : null;
  const goBack = () => navigate(
    previousExercise
      ? `/workout/${session.sessionId}/${previousExercise.index}`
      : `/workout/${session.sessionId}/warmup`,
  );

  // ── Phase 1: choose the variation ───────────────────────────────────────
  if (picking) {
    return (
      <>
      <SiteHeader back={{ label: session.dayLabel, to: `/workout/${session.sessionId}` }} />
      <main className="page page--narrow">
        <p className="muted">Exercise {exercise.index} of {session.exercises.length}</p>
        <h1>Choose your variation</h1>

        <ul className="plain-list">
          {exercise.options.map((opt, i) => (
            <li key={opt} className="choice-line">
              <button
                type="button"
                className={`choice-row${selected === opt ? ' choice-row--selected' : ''}`}
                onClick={() => setSelected(opt)}
              >
                <span className="choice-radio" aria-hidden="true" />
                <span className="choice-body">
                  <span>{opt}</span>
                  {i === 0 && <span className="badge">recommended</span>}
                </span>
              </button>
              <VideoButton exerciseName={opt} />
            </li>
          ))}
        </ul>

        {/* Two rows of label/value pairs:
              Working sets | n | Reps | 8-10
              Warm-up sets | n | Rest | 2:00-3:00
            All four always render so the grid keeps its shape; a missing
            value shows a dash rather than collapsing the layout. */}
        <dl className="spec-grid">
          <dt>Working sets</dt><dd>{workingSets.length}</dd>
          <dt>Reps</dt><dd>{exercise.targetReps ?? '—'}</dd>
          <dt>Warm-up sets</dt><dd>{warmupSets.length}</dd>
          <dt>Rest</dt>
          <dd>
            {exercise.restLowSecs == null
              ? '—'
              : `${formatSecs(exercise.restLowSecs)}–${formatSecs(exercise.restHighSecs ?? exercise.restLowSecs)}`}
          </dd>
        </dl>

        {exercise.notes && (
          <section className="notes-panel">
            <h3>Notes</h3>
            <p>{exercise.notes}</p>
          </section>
        )}

        {exercise.technique && (
          <section className="technique-line">
            <span className="technique-tag">Last set</span>
            <span>{exercise.technique}</span>
            {explained && <InfoButton techniqueId={explained.id} label={explained.title} />}
          </section>
        )}

        {pyramid.length > 0 && (
          <section className="warmup-plan">
            <h3>Warm-up plan</h3>
            <ul className="warmup-plan-list">
              {pyramid.map((rung, i) => (
                <li key={i}>
                  <span className="warmup-tag">W{i + 1}</span>
                  <span>{formatPct(rung.pct)} of working weight</span>
                  <span className="muted">{rung.reps} reps</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <button type="button" className="btn-primary btn-block btn-lg" style={{ marginTop: 20 }} onClick={chooseAndStart}>
          Start exercise
        </button>
        <button type="button" className="btn-quiet btn-block" style={{ marginTop: 10 }} onClick={() => void finishExercise(true)}>
          Skip this exercise
        </button>
      </main>
      </>
    );
  }

  // ── Phase 2: log the sets ───────────────────────────────────────────────
  return (
    <>
    <SiteHeader back={{ label: session.dayLabel, to: `/workout/${session.sessionId}` }} />
    <main className="page page--narrow">
      {saveError && <p className="warn">{saveError} — your last change may not be stored.</p>}

      <p className="muted">Exercise {exercise.index} of {session.exercises.length}</p>
      <h1 className="exercise-title">
        {exercise.exerciseName}
        <VideoButton exerciseName={exercise.exerciseName} />
      </h1>
      <p className="muted" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span>{exercise.targetReps ? `Target ${exercise.targetReps} reps` : 'Log your sets'}</span>
        <button type="button" className="btn-quiet btn-xs" onClick={() => setPicking(true)}>
          Change variation
        </button>
      </p>

      {exercise.notes && (
        <section className="notes-panel">
          <h3>Notes</h3>
          <p>{exercise.notes}</p>
        </section>
      )}

      {/* Always rendered, so the layout never shifts when a rest starts or
          ends. Idle simply shows the next thing to do. */}
      <section
        className={`rest-timer rest-timer--${rest.phase}`}
        aria-live="polite"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(rest.progress * 100)}
      >
        {/* Keyed on the rest's start time as well as its phase: finishing the
            next set mid-rest starts a new rest without changing the phase, and
            the bar must restart from the left with it. */}
        {rest.phase !== 'idle' && (
          <RestFill
            key={`${rest.startedAt}-${rest.phase}`}
            total={rest.segmentTotal}
            elapsed={rest.segmentElapsed}
            mode={rest.phase === 'ready' ? 'drain' : 'fill'}
          />
        )}
        <div className="rest-timer-row">
          <span className="rest-timer-time">
            {rest.phase === 'over' ? `+${formatSecs(rest.overBy)}` : formatSecs(rest.countdown)}
          </span>
          <span className="rest-timer-label">
            {rest.phase === 'idle' && upNext}
            {rest.phase === 'resting' && 'Resting — until you can start'}
            {rest.phase === 'ready' && upNext}
            {rest.phase === 'over' && `${upNext} — past the prescribed rest`}
          </span>
          {/* No dismiss while actually resting: an accidental tap there would
              throw away the countdown the lifter is relying on. */}
          {(rest.phase === 'ready' || rest.phase === 'over') && (
            <button
              type="button"
              className="btn-quiet btn-sm"
              onClick={() => {
                rest.stop();
                setRestWindow(null);
                if (sessionId) clearJson(restWindowKey(sessionId));
              }}
            >
              Dismiss
            </button>
          )}
        </div>
      </section>

      {previousSets.length > 0 && (
        <p className="prev-note">
          Pre-filled from {previousPerformedAt
            ? new Date(previousPerformedAt * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : 'last time'} — green beats it, red is under.
        </p>
      )}

      <div className="set-head">
        <span />
        <span>Weight</span>
        <span>Reps</span>
        <span />
      </div>

      <ul className="set-list">
        {exercise.sets.map(set => {
          const isNext = nextSet?.setNumber === set.setNumber;
          const warmupPos = warmupSets.findIndex(s => s.setNumber === set.setNumber);
          const rung = warmupPos >= 0 ? pyramid[warmupPos] : undefined;
          const workingNumber = set.warmup
            ? null
            : workingSets.findIndex(s => s.setNumber === set.setNumber) + 1;

          const isLastWarmup = set.warmup && warmupPos === warmupSets.length - 1;
          const prev = previousSetFor(set, exercise.sets, previousSets);

          return (
            <Fragment key={set.setNumber}>
            <li
              className={`set-row${set.completed ? ' set-row--done' : ''}${isNext ? ' set-row--next' : ''}${set.warmup ? ' set-row--warmup' : ''}`}
            >
              <div className="set-row-main">
                <span className={`set-row-label${set.warmup ? ' set-row-label--warmup' : ''}`}>
                  {set.warmup ? `W${warmupPos + 1}` : workingNumber}
                </span>

                <Stepper
                  label={`set ${set.setNumber} weight`}
                  value={set.weight ?? null}
                  step={WEIGHT_STEP}
                  disabled={set.completed}
                  onStep={d => stepWeight(set, d)}
                  hold
                  trend={compareToPrevious(set.weight, prev?.weight)}
                />

                <Stepper
                  label={`set ${set.setNumber} reps`}
                  value={shownReps(set)}
                  step={1}
                  disabled={set.completed}
                  onStep={d => stepReps(set, d)}
                  trend={compareToPrevious(shownReps(set), prev?.reps)}
                />

                {set.completed ? (
                  <button
                    type="button"
                    className="btn-quiet btn-sm"
                    onClick={() => updateSets(sets => sets.map(s =>
                      s.setNumber === set.setNumber
                        ? { ...s, completed: false, completedAt: undefined }
                        : s), true)}
                  >
                    Undo
                  </button>
                ) : (
                  <button type="button" className="btn-primary btn-sm" onClick={() => completeSet(set)}>
                    Done
                  </button>
                )}
              </div>

              {rung && (
                <div className="set-row-warmup">
                  <span className="warmup-tag">Warm-up</span>
                  <span className="muted">{formatPct(rung.pct)} · {rung.reps} reps</span>
                  {/* Only the warm-up sets beyond the prescribed minimum can go. */}
                  {set.warmup && warmupPos >= warmupMin && !set.completed && (
                    <button
                      type="button"
                      className="btn-quiet btn-xs row-remove"
                      onClick={() => resize(true, warmupSets.length - 1)}
                      aria-label={`Remove warm-up set ${warmupPos + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}

              {/* The technique applies to the last working set only. */}
              {exercise.technique && isLastWorking(set) && (
                <div className="set-row-technique">
                  <span className="technique-tag">Last set</span>
                  <span>{exercise.technique}</span>
                  {explained && <InfoButton techniqueId={explained.id} label={explained.title} />}
                </div>
              )}
            </li>

            {/* Directly beneath the warm-up rows and above the working sets. */}
            {isLastWarmup && warmupSets.length < warmupMax && (
              <li className="add-warmup-row">
                <button
                  type="button"
                  className="btn-quiet btn-block btn-sm add-warmup"
                  onClick={() => resize(true, warmupSets.length + 1)}
                >
                  + Add warm-up set
                </button>
              </li>
            )}
            </Fragment>
          );
        })}
      </ul>


      <div className="page-actions">
        <button type="button" className="btn-secondary" onClick={goBack}>
          {previousExercise ? '← Previous' : '← Warm-up'}
        </button>
        <button
          type="button"
          className="btn-primary btn-lg"
          onClick={() => void finishExercise(false)}
        >
          {allSetsDone ? 'Next exercise →' : 'Finish exercise →'}
        </button>
      </div>
    </main>
    </>
  );
}
